import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FiPlusCircle, FiMinusCircle, FiInfo } from "react-icons/fi";
import { fetchPlanDetailsAction } from "../../../redux/actions/planActions";
import { toast } from "react-toastify";

const PlansDetails = ({
  planDetails,
  setPlanDetails,
  register,
  setValue,
  errors,
  editingPartner,
  clearErrors,
}) => {
  const dispatch = useDispatch();
  const {
    planDetails: apiPlanDetails,
    loading,
    error,
  } = useSelector((state) => state.plan || {});
  const [lastQueriedId, setLastQueriedId] = useState(null);
  const [loadingForId, setLoadingForId] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [formattedAmounts, setFormattedAmounts] = useState({});


  const getCurrencySymbol = (currency) => {
    const symbols = {
      USD: '$',
      JPY: '¥',
      EUR: '€',
      GBP: '£',
      SGD: 'S$',
      HKD: 'HK$',
      AUD: 'A$'
    };
    return symbols[currency] || currency;
  };

  const formatAmountForDisplay = (amount, currency) => {
    if (!amount) return '';
    const numAmount = parseFloat(amount);
    // JPY doesn't use cents, so don't divide by 100
    if (currency === 'JPY') {
      return numAmount.toString();
    }
    // For USD and other currencies, divide by 100 to convert from cents
    const value = numAmount / 100;
    return value % 1 === 0 ? value.toString() : value.toFixed(2);
  };

  const formatAmountForSubmit = (amount, currency) => {
    if (!amount) return '';
    // JPY doesn't use cents
    if (currency === 'JPY') {
      return amount.toString();
    }
    // Convert back to cents for other currencies
    return Math.round(parseFloat(amount) * 100).toString();
  };

  const handleStripeProductIdChange = async (index, value) => {
    // Clear validation error for this field when user starts typing
    const oldValue = planDetails[index].stripeProductId;
    if (oldValue && validationErrors[oldValue]) {
      setValidationErrors((prev) => ({ ...prev, [oldValue]: null }));
    }

    // Check for duplicates
    const duplicateIndex = planDetails.findIndex(
      (plan, i) =>
        i !== index &&
        plan.stripeProductId.trim() === value.trim() &&
        value.trim() !== ""
    );

    if (duplicateIndex !== -1) {
      toast.error("Duplicate Stripe Product ID is not allowed");
      return;
    }

    const newPlans = [...planDetails];
    newPlans[index].stripeProductId = value;

    if (!value.trim()) {
      // Clear other fields when Stripe Product ID is removed
      newPlans[index].amount = "";
      newPlans[index].planName = "";
      newPlans[index].couponDetails = "";
      setValue(`planDetails.${index}.amount`, "");
      setValue(`planDetails.${index}.planName`, "");
      setValue(`planDetails.${index}.couponDetails`, "");
    }

    setPlanDetails(newPlans);
    setValue("planDetails", newPlans);

    if (value.trim()) {
      setLastQueriedId(value);
      setLoadingForId(value);
      dispatch(fetchPlanDetailsAction([value]));
    }
  };
  useEffect(() => {
    if (apiPlanDetails !== null) {
      // Handle single object response
      if (apiPlanDetails && apiPlanDetails.stripeProductId) {
        const updatedPlans = [...planDetails];
        const planIndex = updatedPlans.findIndex(
          (p) => p.stripeProductId === apiPlanDetails.stripeProductId
        );
        if (planIndex !== -1) {
          const currency = (apiPlanDetails.prices?.[0]?._doc?.currency || apiPlanDetails.prices?.[0]?.currency)?.toUpperCase() || "USD";
          // Get amount from __parentArray or _doc, then format for display
          const rawAmount = apiPlanDetails.prices?.[0]?.__parentArray?.[0]?.amount || apiPlanDetails.prices?.[0]?._doc?.amount || apiPlanDetails.prices?.[0]?.amount;
          const displayAmount = formatAmountForDisplay(rawAmount, currency);
          
          updatedPlans[planIndex] = {
            ...updatedPlans[planIndex],
            amount: rawAmount, // Store raw amount
            planName: apiPlanDetails.name || "",
            currency: currency,
          };
          setPlanDetails(updatedPlans);
          setValue(
            `planDetails.${planIndex}.amount`,
            rawAmount // Store raw amount in form
          );
          
          // Store formatted amount for display
          const key = `${planIndex}-${apiPlanDetails.stripeProductId}`;
          setFormattedAmounts(prev => ({ ...prev, [key]: displayAmount }));
          setValue(
            `planDetails.${planIndex}.planName`,
            apiPlanDetails.name || ""
          );
          // Clear validation errors for auto-populated fields
          if (clearErrors) {
            clearErrors(`planDetails.${planIndex}.amount`);
            clearErrors(`planDetails.${planIndex}.planName`);
          }
          setValidationErrors((prev) => ({ ...prev, [lastQueriedId]: null }));
        }
        setLoadingForId(null);
      } else if (lastQueriedId) {
        // Clear fields only for the specific Stripe Product ID that was queried
        const updatedPlans = [...planDetails];
        const planIndex = updatedPlans.findIndex(
          (p) => p.stripeProductId === lastQueriedId
        );
        if (planIndex !== -1) {
          updatedPlans[planIndex] = {
            ...updatedPlans[planIndex],
            amount: "",
            planName: "",
          };
          setValue(`planDetails.${planIndex}.amount`, "");
          setValue(`planDetails.${planIndex}.planName`, "");
          setPlanDetails(updatedPlans);
        }
        setLastQueriedId(null);
        setLoadingForId(null);
      }
    }

    // Handle API errors
    if (error && lastQueriedId) {
      setValidationErrors((prev) => ({
        ...prev,
        [lastQueriedId]: "Please put a valid stripeProductId",
      }));
      setLoadingForId(null);
    }
  }, [apiPlanDetails, error]);

  // Format amounts for display when editingPartner changes
  useEffect(() => {
    if (editingPartner && planDetails.length > 0) {
      const newFormattedAmounts = {};
      planDetails.forEach((plan, index) => {
        if (plan.amount && plan.currency) {
          const key = `${index}-${plan.stripeProductId}`;
          newFormattedAmounts[key] = formatAmountForDisplay(plan.amount, plan.currency);
        }
      });
      setFormattedAmounts(newFormattedAmounts);
    } else {
      setFormattedAmounts({});
    }
  }, [editingPartner, planDetails]);

  return (
    <>
      <div className="border p-2 rounded-3 bg-gray mb-2">
        {/* Header row with labels and controls */}
        <div className="d-flex align-items-center justify-content-between mb-1">
          <h6 className="mb-0">Plan Details</h6>
          <div className="d-flex align-items-center gap-2">
            <FiPlusCircle
              className="info_pointer text-primary fw-bold"
              onClick={() => {
                const newPlans = [
                  ...planDetails,
                  {
                    stripeProductId: "",
                    amount: "",
                    planName: "",
                    couponDetails: "",
                  },
                ];
                setPlanDetails(newPlans);
                setValue("planDetails", newPlans);
              }}
              title="Add another plan"
              style={{ fontSize: "16px", cursor: "pointer" }}
            />
            {planDetails.length > 1 && (
              <FiMinusCircle
                className="info_pointer text-danger fw-bold"
                onClick={() => {
                  const newPlans = planDetails.slice(0, -1);
                  setPlanDetails(newPlans);
                  setValue("planDetails", newPlans);
                }}
                title="Remove last plan"
                style={{ fontSize: "16px", cursor: "pointer" }}
              />
            )}
          </div>
        </div>
        
        {/* Column headers */}
        <div className="row mb-0">
          <div className="col-md-3 form-group">
            <label className="fw-bold">
              Stripe Product ID <span className="text-danger">*</span>
              <span
                className="text-secondary info_pointer ms-1"
                title="Please use the Stripe Product ID provided by Stripe, which you can retrieve directly from your Stripe dashboard."
              >
                <FiInfo />
              </span>
            </label>
          </div>
          <div className="col-md-3 form-group">
            <label className="fw-bold">
              Plan Amount <span className="text-danger">*</span>
            </label>
          </div>
          <div className="col-md-3 form-group">
            <label className="fw-bold">
              Plan Name <span className="text-danger">*</span>
            </label>
          </div>
          <div className="col-md-3 form-group">
            <label className="fw-bold">Coupon Details</label>
          </div>
        </div>

        {planDetails.map((plan, index) => (
          <div key={index} className="plan-details-section mb-2">
            <div className="row">
              <div className="col-md-3">
                <div className="form-group">
                  <input
                    {...register(`planDetails.${index}.stripeProductId`, {
                      required: "Stripe Product ID is required",
                      validate: (value) =>
                        (value && value.toString().trim() !== "") ||
                        "Stripe Product ID is required",
                    })}
                    value={plan.stripeProductId}
                    onChange={(e) => {
                      handleStripeProductIdChange(index, e.target.value);
                      // Clear react-hook-form error when user starts typing
                      if (e.target.value.trim()) {
                        clearErrors && clearErrors(`planDetails.${index}.stripeProductId`);
                      }
                    }}
                    className={`form-input ${
                      errors?.planDetails?.[index]?.stripeProductId
                        ? "border-danger"
                        : ""
                    } ${
                      editingPartner?.status === "inactive" ? "disabled" : ""
                    }`}
                    placeholder="Enter stripe product ID"
                    disabled={editingPartner?.status === "inactive"}
                    style={{
                      backgroundColor:
                        editingPartner?.status === "inactive" ? "#f8f9fa" : "",
                    }}
                  />
                  {errors?.planDetails?.[index]?.stripeProductId && (
                    <span className="error-text" style={{ fontSize: "12px" }}>
                      {errors.planDetails[index].stripeProductId?.message}
                    </span>
                  )}
                  {validationErrors[plan.stripeProductId] && (
                    <span
                      className="error-text"
                      style={{ fontSize: "12px", color: "#ef4444" }}
                    >
                      {validationErrors[plan.stripeProductId]}
                    </span>
                  )}
                </div>
              </div>

              <div className="col-md-3">
                <div className="form-group">
                  <div className="position-relative">
                    {plan.currency && (
                      <span className="position-absolute" style={{
                        left: "8px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        color: "#666",
                        fontSize: "14px",
                        fontWeight: "bold",
                        zIndex: 1,
                        pointerEvents: "none",
                        borderRight: "1px solid #ddd",
                        paddingRight: "8px"
                      }}>
                        {getCurrencySymbol(plan.currency)}
                      </span>
                    )}
                    <input
                      {...register(`planDetails.${index}.amount`, {
                        required: "Plan amount is required",
                        validate: (value) =>
                          (value && value.toString().trim() !== "") ||
                          "Plan amount is required",
                      })}
                      value={
                        loadingForId === plan.stripeProductId && loading
                          ? "loading..."
                          : plan.currency && plan.amount
                            ? formattedAmounts[`${index}-${plan.stripeProductId}`] || formatAmountForDisplay(plan.amount, plan.currency)
                            : plan.amount
                      }
                      onChange={(e) => {
                        const inputValue = e.target.value;
                        const key = `${index}-${plan.stripeProductId}`;
                        
                        // Update the formatted display value
                        setFormattedAmounts(prev => ({ ...prev, [key]: inputValue }));
                        
                        // Store the raw value for submission (convert back to cents for USD)
                        const newPlans = [...planDetails];
                        const rawValue = editingPartner && plan.currency ? formatAmountForSubmit(inputValue, plan.currency) : inputValue;
                        newPlans[index].amount = rawValue;
                        setPlanDetails(newPlans);
                        setValue(`planDetails.${index}.amount`, rawValue);
                        
                        if (e.target.value.trim()) {
                          clearErrors && clearErrors(`planDetails.${index}.amount`);
                        }
                      }}
                      placeholder="Enter plan amount"
                      className={`form-input w-100 ${
                        errors?.planDetails?.[index]?.amount
                          ? "border-danger"
                          : ""
                      } ${
                        editingPartner?.status === "inactive" ? "disabled" : ""
                      }`}
                      readOnly={loadingForId === plan.stripeProductId && loading}
                      disabled={true}
                      style={{
                        backgroundColor: "#f8f9fa",
                        paddingLeft: plan.currency === 'HKD' ? "50px" : plan.currency ? "35px" : "8px"
                      }}
                    />
                  </div>
                  {errors?.planDetails?.[index]?.amount && (
                    <span className="error-text" style={{ fontSize: "12px" }}>
                      {errors.planDetails[index].amount?.message}
                    </span>
                  )}
                </div>
              </div>

              <div className="col-md-3">
                <div className="form-group">
                  <input
                    {...register(`planDetails.${index}.planName`, {
                      required: "Plan name is required",
                      validate: (value) =>
                        (value && value.toString().trim() !== "") ||
                        "Plan name is required",
                    })}
                    value={
                      loadingForId === plan.stripeProductId && loading
                        ? "loading..."
                        : plan.planName
                    }
                    onChange={(e) => {
                      const newPlans = [...planDetails];
                      newPlans[index].planName = e.target.value;
                      setPlanDetails(newPlans);
                      setValue(`planDetails.${index}.planName`, e.target.value);
                      if (e.target.value.trim()) {
                        clearErrors && clearErrors(`planDetails.${index}.planName`);
                      }
                    }}
                    placeholder="Enter plan name"
                    className={`form-input ${
                      errors?.planDetails?.[index]?.planName
                        ? "border-danger"
                        : ""
                    } ${
                      editingPartner?.status === "inactive" ? "disabled" : ""
                    }`}
                    readOnly={loadingForId === plan.stripeProductId && loading}
                    disabled={true}
                    style={{
                      backgroundColor: "#f8f9fa",
                    }}
                  />
                  {errors?.planDetails?.[index]?.planName && (
                    <span className="error-text" style={{ fontSize: "12px" }}>
                      {errors.planDetails[index].planName?.message}
                    </span>
                  )}
                </div>
              </div>

              <div className="col-md-3">
                <div className="form-group">
                  <input
                    {...register(`planDetails.${index}.couponDetails`)}
                    value={plan.couponDetails}
                    onChange={(e) => {
                      const newPlans = [...planDetails];
                      newPlans[index].couponDetails = e.target.value;
                      setPlanDetails(newPlans);
                      setValue("planDetails", newPlans);
                    }}
                    className={`form-input ${
                      editingPartner?.status === "inactive" ? "disabled" : ""
                    }`}
                    placeholder="Enter coupon details (optional)"
                    disabled={true}
                    style={{
                      backgroundColor: "#f8f9fa",
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

export default PlansDetails;